import { useMemo } from 'react'

import { matchFilterNumber, sortArrayBy } from '@domain-group/javascript-utils'

import { useLoadMore, useWatchEntityUpdates } from '@domain-group/react-hooks'

import useReduxAction from '@hooks/useReduxAction'
import useSelector from '@hooks/useSelector'

import type { AgencyPaymentMethodModel, AgencyPaymentMethodRequestOptions } from '@models/types'

import type { ModuleState } from '@redux/modules/types'

const watchEntityKeys = ['agencyPaymentMethods']

type AgencyPaymentMethodFilters = {
  agency_id?: number,
  string?: string,
}

type UseAgencyPaymentMethodsOptions = {
  filters?: AgencyPaymentMethodFilters,
  performHttpRequests?: boolean,
  requestOptions?: AgencyPaymentMethodRequestOptions,
}

function useAgencyPaymentMethods(options: UseAgencyPaymentMethodsOptions) {
  const { filters = {}, requestOptions } = options
  const {
    agency_id: filterAgencyId,
  } = filters

  const {
    updatedEntities: { agencyPaymentMethods: agencyPaymentMethodsUpdatedAt },
  } = useWatchEntityUpdates(watchEntityKeys)

  const { agencyPaymentMethods, paymentMethods } = useSelector(reduxState => reduxState.entities)

  const filteredAgencyPaymentMethods = useMemo(() => {
    const filtered = Object.values(agencyPaymentMethods).filter((agencyPaymentMethod: AgencyPaymentMethodModel) => {
      const { agency_id } = agencyPaymentMethod

      const matchAgencyId = matchFilterNumber(agency_id, filterAgencyId)

      return matchAgencyId
    })

    return sortArrayBy(filtered, 'asc')
  }, [agencyPaymentMethodsUpdatedAt, JSON.stringify(filters)])

  const filteredAgencyPaymentMethodsCount = filteredAgencyPaymentMethods.length
  const hasFilteredAgencyPaymentMethods = !!filteredAgencyPaymentMethodsCount

  const paymentMethodsArray = Object.values(paymentMethods)

  const currentPaymentMethodIds = filteredAgencyPaymentMethods.map(
    agencyPaymentMethod => agencyPaymentMethod.payment_method_id,
  )

  const availablePaymentMethods = paymentMethodsArray.filter(paymentMethod => (
    !currentPaymentMethodIds.includes(paymentMethod.id)
  ))

  const hasAvailablePaymentMethods = availablePaymentMethods?.length > 0

  const loadMorePayload = useLoadMore({
    filters,
    loadedCount: filteredAgencyPaymentMethodsCount,
    performHttpRequests: options.performHttpRequests,
  })

  const {
    callbacks: { loadMore },
    canLoadMore,
    filtersWithOffset,
    limit,
    performHttpRequests,
  } = loadMorePayload

  const { loading: loadingAgencyPaymentMethods } = useReduxAction(
    'agencyPaymentMethods',
    'loadAgencyPaymentMethods',
    {
      ...requestOptions,
      ...filtersWithOffset,
      limit,
    },
    [filtersWithOffset, performHttpRequests],
    {
      shouldPerformFn: ({ loading }: ModuleState) => performHttpRequests && !loading,
    },
  )

  return {
    availablePaymentMethods,
    callbacks: {
      loadMore,
    },
    canLoadMore,
    filteredAgencyPaymentMethods,
    filteredAgencyPaymentMethodsCount,
    hasAvailablePaymentMethods,
    hasFilteredAgencyPaymentMethods,
    paymentMethods,
    loading: loadingAgencyPaymentMethods,
  }
}

export default useAgencyPaymentMethods
